import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import {
  AlertTriangle, ChevronDown, ChevronRight, CheckCircle2, Copy, ExternalLink,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api.js';
import { draftLpr, dispatchLpr, previewLprPdf } from '@/services/lpr.service.js';
import { cn } from '@/lib/utils.js';
import { formatHumanDate } from '@/lib/format.js';
import PolicyInfoDialog from './PolicyInfoDialog.jsx';
import { formatDateTime } from '@/lib/date-display.js';

// ── LPRSheet — Lost Policy Release wizard (LPR-WIZARD-SPEC.md §4) ────
//
// Broker-facing surface that:
//   1. Pulls fresh deal context from /api/tracon/deal/:id when the sheet
//      opens (display-only — the populated PDF is the source of truth).
//   2. Collects cancellation parameters in a 1- or 2-column form.
//   3. Renders a live ACORD 35 preview in an iframe via POST
//      /api/lpr/deal/:id/preview returning a PDF blob (debounced 600ms,
//      object URL revoked on cleanup so memory doesn't leak as the
//      broker iterates).
//   4. Save draft → POST /api/lpr/deal/:id/draft (no Tower session,
//      no email).
//   5. Send for signature → POST /api/lpr/deal/:id/dispatch. Server
//      mints a Tower token, uploads the unsigned PDF to WorkDrive,
//      writes policy_transactions (status=pending_signature), emits
//      lpr.draft.created → lpr.draft.dispatched, sends the insured an
//      email with the Tower link.
//
// Hard rules honored here (LPR-WIZARD-SPEC.md):
//   #2 Desktop-primary, NOT mobile-first.
//   #3 NEVER computes / quotes a return premium amount in any UI
//      affordance. Basis radios DO surface a plain-English description
//      because the client sees this on the Tower signing view.
//      Amendment (LPR-PARTIAL-CANCEL-AND-ROSTER-GATE-SPEC.md piece 1):
//      the broker may TYPE premium figures (full term override, return
//      premium, unearned factor) — pass-through values, never computed.
//      Blank still means the carrier fills them.
//   #8 broker_notes is broker-only. UI labels it "internal · prints on
//      REMARKS"; server-side scrubbing keeps it out of the Tower /info
//      response and the client-facing email body.

const BASIS_OPTIONS = [
  { key: 'pro_rata',         label: 'Pro-rata',         desc: 'Refund the unearned portion of the premium' },
  { key: 'short_rate',       label: 'Short-rate',       desc: 'Carrier applies a short-rate penalty to the unearned premium' },
  { key: 'flat',             label: 'Flat',             desc: 'Full return of premium' },
  { key: 'subject_to_audit', label: 'Subject to audit', desc: 'Refund calculated after the carrier audits actual exposures' },
];

const REASON_OPTIONS = [
  { key: 'insured_request', label: 'Requested by insured' },
  { key: 'not_taken',       label: 'Not taken (policy never put in force)' },
  { key: 'rewritten',       label: 'Rewritten (replaced with another policy)' },
  { key: 'aircraft_sold',   label: 'Aircraft sold' },
  { key: 'other',           label: 'Other' },
];

const DISTRIBUTION_OPTIONS = [
  { key: 'insured',              label: 'Insured' },
  { key: 'loss_payee',           label: 'Loss Payee' },
  { key: 'lenders_loss_payable', label: "Lender's Loss Payable" },
  { key: 'mortgagee',            label: 'Mortgagee' },
  { key: 'lienholder',           label: 'Lienholder' },
  { key: 'company',              label: 'Company (carrier)' },
  { key: 'finance_company',      label: 'Finance Company' },
  { key: 'other',                label: 'Other' },
];

const inputClass = cn(
  'flex min-h-[44px] w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text',
  'placeholder:text-text-dim',
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-bg',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

function todayYMD() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function defaultInputs() {
  return {
    cancellation_effective_date: todayYMD(),
    cancellation_effective_time: '12:01',
    cancellation_effective_meridian: 'AM',
    basis: 'short_rate',
    subject_to_audit: false,
    reason: 'insured_request',
    other_description: '',
    new_insurer_name: '',
    broker_notes: '',
    full_term_premium: '',
    return_premium: '',
    unearned_factor: '',
    // Scope (LPR-PARTIAL-CANCEL-AND-ROSTER-GATE-SPEC.md piece 3): whole
    // policy release (legacy) or a removal endorsement for specific tails.
    scope: 'policy',
    scope_tails: [],
    distribution: {
      insured: true, loss_payee: false, lenders_loss_payable: false,
      mortgagee: false, lienholder: false, company: true,
      finance_company: false, other: false, other_description: '',
    },
  };
}

export default function LPRSheet({ dealId, open, onOpenChange, onChange, prefill }) {
  // Deal context — pulled when the sheet opens. Display-only; the
  // server's data mapper resolves authoritative values for the PDF.
  const [dealCtx, setDealCtx] = useState(null);
  const [dealCtxError, setDealCtxError] = useState(null);
  const [policyCardOpen, setPolicyCardOpen] = useState(true);
  // Policy info door (TRACON-FIELD-GAP-INVENTORY.md): the same dialog the
  // workspace action bar mounts. Opened from the policy card's Edit button
  // or from the preview's missing-field error. ctxRefresh bumps after a
  // save so the deal context AND the live preview re-resolve fresh values.
  const [policyInfoOpen, setPolicyInfoOpen] = useState(false);
  const [ctxRefresh, setCtxRefresh] = useState(0);

  // Form state — single object so the debounced preview useEffect can
  // depend on a stable JSON serialization.
  const [inputs, setInputs] = useState(defaultInputs);

  // Aircraft on the policy — fetched lazily for the scope tail-picker
  // (same prefill endpoint RosterSheet uses; subform-accurate tails).
  const [aircraftOptions, setAircraftOptions] = useState(null);

  // Live preview state.
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const previewUrlRef = useRef(null);

  // Save / dispatch state.
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [done, setDone] = useState(null);

  // Reset when the sheet opens/closes.
  useEffect(() => {
    if (!open) {
      // Defer reset until the sheet's exit animation finishes so the
      // broker doesn't see fields blanking out.
      const t = setTimeout(() => {
        setInputs(defaultInputs());
        setDone(null);
        setFormError(null);
        setPreviewUrl(null);
        setAircraftOptions(null);
        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
          previewUrlRef.current = null;
        }
      }, 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Fetch deal context when the sheet opens.
  useEffect(() => {
    if (!open || !dealId) return;
    setDealCtx(null);
    setDealCtxError(null);
    api
      .get(`/api/tracon/deal/${encodeURIComponent(dealId)}`)
      .then((res) => setDealCtx(res?.deal || res || null))
      .catch((err) => setDealCtxError(err?.body?.error || err?.message || 'Could not load deal'));
  }, [open, dealId, ctxRefresh]);

  // Prefill hand-off (roster gate → scoped LPR): seed scope + tails when
  // the sheet opens with a prefill payload.
  useEffect(() => {
    if (!open || !prefill) return;
    setInputs((prev) => ({
      ...prev,
      scope: prefill.scope === 'aircraft' ? 'aircraft' : prev.scope,
      scope_tails: Array.isArray(prefill.scope_tails) ? prefill.scope_tails : prev.scope_tails,
      // Aircraft removal is an endorsement request — reason defaults to
      // insured request; broker adjusts if it is a not-taken.
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill]);

  // Lazy tail-picker source: fetch the roster only when aircraft scope is
  // active and we have not loaded it yet.
  useEffect(() => {
    if (!open || !dealId || inputs.scope !== 'aircraft' || aircraftOptions !== null) return;
    api
      .get(`/api/tracon/command-center/deals/${encodeURIComponent(dealId)}/pre-stage-prefill`)
      .then((res) => {
        const tails = (res?.aircraft || [])
          .map((a) => String(a.tail_number || '').trim().toUpperCase())
          .filter(Boolean);
        setAircraftOptions(tails);
      })
      .catch(() => setAircraftOptions([]));
  }, [open, dealId, inputs.scope, aircraftOptions]);

  // Debounced live preview. Fires 600ms after the broker stops typing.
  const inputsKey = useMemo(() => JSON.stringify(inputs), [inputs]);
  useEffect(() => {
    if (!open || !dealId || done) return undefined;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    const timer = setTimeout(async () => {
      try {
        const blob = await previewLprPdf(dealId, inputs);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        // Revoke the previous URL only AFTER we set the new one so the
        // iframe doesn't briefly point at a revoked URL.
        const prev = previewUrlRef.current;
        previewUrlRef.current = url;
        setPreviewUrl(url);
        if (prev) URL.revokeObjectURL(prev);
      } catch (err) {
        if (!cancelled) setPreviewError(err?.message || 'Preview failed');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dealId, inputsKey, done, ctxRefresh]);

  // Final cleanup — revoke any lingering object URL when the component
  // unmounts. Separate effect so the cleanup doesn't run on every
  // inputs change (would race with the iframe).
  useEffect(() => () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  function setField(key, value) {
    setInputs((prev) => ({ ...prev, [key]: value }));
  }
  function setDistField(key, value) {
    setInputs((prev) => ({ ...prev, distribution: { ...prev.distribution, [key]: value } }));
  }
  function toggleScopeTail(tail) {
    setInputs((prev) => {
      const has = prev.scope_tails.includes(tail);
      return {
        ...prev,
        scope_tails: has ? prev.scope_tails.filter((t) => t !== tail) : [...prev.scope_tails, tail],
      };
    });
  }

  function validate() {
    if (!dealId) return 'No deal selected.';
    if (!inputs.cancellation_effective_date) return 'Cancellation effective date is required.';
    if (inputs.scope === 'aircraft' && inputs.scope_tails.length === 0) {
      return 'Pick at least one aircraft to remove (or switch scope to Entire policy).';
    }
    if (inputs.reason === 'rewritten' && !inputs.new_insurer_name.trim()) {
      return 'New insurer name required when reason is Rewritten.';
    }
    if (inputs.reason === 'other' && !inputs.other_description.trim()) {
      return 'Description required when reason is Other.';
    }
    for (const [key, label] of [['full_term_premium', 'Full term premium'], ['return_premium', 'Return premium']]) {
      const raw = String(inputs[key] ?? '').trim();
      if (raw !== '' && (!Number.isFinite(Number(raw)) || Number(raw) < 0)) {
        return `${label} must be a non-negative number (or blank).`;
      }
    }
    return null;
  }

  async function handleSaveDraft() {
    const v = validate();
    if (v) { setFormError(v); return; }
    setFormError(null);
    setSavingDraft(true);
    try {
      const res = await draftLpr(dealId, inputs);
      if (!res?.success) throw new Error(res?.error || 'Save failed');
      toast.success('LPR draft saved');
      for (const w of res.warnings || []) toast.info(w);
      if (typeof onChange === 'function') onChange();
    } catch (err) {
      const msg = err instanceof ApiError ? err.body?.error || err.code : err.message;
      setFormError(msg || 'Save failed');
    } finally {
      setSavingDraft(false);
    }
  }

  async function handleDispatch() {
    const v = validate();
    if (v) { setFormError(v); return; }
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await dispatchLpr(dealId, inputs);
      if (!res?.success) throw new Error(res?.error || 'Dispatch failed');
      setDone(res);
      toast.success(
        res.emailed_to
          ? `Release sent to ${res.emailed_to}`
          : 'Release generated — copy & share the Tower link manually'
      );
      for (const w of res.warnings || []) toast.info(w);
      if (typeof onChange === 'function') onChange();
    } catch (err) {
      const msg = err instanceof ApiError ? err.body?.error || err.code : err.message;
      setFormError(msg || 'Dispatch failed');
    } finally {
      setSubmitting(false);
    }
  }

  function copyTowerLink() {
    if (!done?.tower_url) return;
    navigator.clipboard.writeText(done.tower_url)
      .then(() => toast.success('Tower link copied'))
      .catch(() => toast.error('Copy failed'));
  }

  const insured = dealCtx?.account?.account_name
    || dealCtx?.named_insured
    || dealCtx?.Account_Name?.name
    || '';
  // Prefer underwriter / market display name (e.g., "Beacon") — that's
  // the name the broker and insured both recognize. Fall back to the
  // legal carrier entity name only when no underwriter is on file.
  const carrier = dealCtx?.underwriter?.name
    || dealCtx?.Underwriter?.name
    || (typeof dealCtx?.Underwriter === 'string' ? dealCtx.Underwriter : null)
    || dealCtx?.carrier?.name
    || dealCtx?.Carrier?.name
    // /api/tracon/deal/:id returns `carrier` as a flat string
    // (deal.Underwriter || deal.Carrier?.name || session.carrier_name).
    || (typeof dealCtx?.carrier === 'string' ? dealCtx.carrier : null)
    || '';
  const policyNumber = dealCtx?.policy_number || dealCtx?.Policy_Number || '';
  const policyStart = formatHumanDate(dealCtx?.policy_start || dealCtx?.Policy_Start || '');
  const policyEnd = formatHumanDate(dealCtx?.policy_end || dealCtx?.Policy_End_Date || dealCtx?.policy_expiration || '');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="flex max-h-[96vh] flex-col overflow-hidden md:mx-auto md:max-w-6xl"
      >
        <SheetHeader>
          <SheetTitle>
            Lost Policy Release {insured ? `— ${insured}` : ''}
          </SheetTitle>
          <SheetDescription>
            {policyNumber
              ? `Policy ${policyNumber}${carrier ? ` · ${carrier}` : ''}`
              : 'Cancellation request for the insured to sign in Tower'}
          </SheetDescription>
        </SheetHeader>

        {dealCtxError ? (
          <div className="m-4 rounded-card border border-warning bg-warning-soft px-3 py-2 text-xs text-warning">
            {dealCtxError}
          </div>
        ) : null}

        {done ? (
          <SuccessBody done={done} carrier={carrier} onCopy={copyTowerLink} scoped={inputs.scope === 'aircraft'} />
        ) : (
          <div className="flex-1 overflow-y-auto px-1 py-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* ── Left column: form ─────────────────────────────── */}
              <div className="space-y-4">
                <PolicyCard
                  expanded={policyCardOpen}
                  onToggle={() => setPolicyCardOpen((v) => !v)}
                  insured={insured}
                  carrier={carrier}
                  policyNumber={policyNumber}
                  policyStart={policyStart}
                  policyEnd={policyEnd}
                  onEdit={() => setPolicyInfoOpen(true)}
                />

                <FieldSet legend="Scope">
                  <RadioGroup
                    name="lpr-scope"
                    value={inputs.scope}
                    options={[
                      { key: 'policy', label: 'Entire policy', desc: 'Whole-policy release, coverage ends on the date below' },
                      { key: 'aircraft', label: 'Remove aircraft', desc: 'Removal endorsement, the policy continues without the selected aircraft. The roster updates after the carrier confirms the endorsement.' },
                    ]}
                    onChange={(v) => setField('scope', v)}
                  />
                  {inputs.scope === 'aircraft' ? (
                    <div className="mt-2 rounded-card border border-border bg-bg p-2">
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                        Aircraft to remove
                      </div>
                      {aircraftOptions === null ? (
                        <div className="text-[11px] text-text-dim">Loading roster…</div>
                      ) : aircraftOptions.length === 0 ? (
                        <div className="text-[11px] text-text-dim">
                          No roster found — tails from the hand-off are kept below.
                        </div>
                      ) : null}
                      <div className="flex flex-wrap gap-2">
                        {[...new Set([...(aircraftOptions || []), ...inputs.scope_tails])].map((tail) => (
                          <label key={tail} className="flex items-center gap-1.5 rounded-card border border-border px-2 py-1 text-sm text-text">
                            <input
                              type="checkbox"
                              checked={inputs.scope_tails.includes(tail)}
                              onChange={() => toggleScopeTail(tail)}
                              className="h-4 w-4 rounded border-border bg-bg accent-accent"
                            />
                            <span className="font-mono">{tail}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </FieldSet>

                <FieldSet legend="When">
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="Effective date" className="col-span-2">
                      <Input
                        type="date"
                        value={inputs.cancellation_effective_date}
                        onChange={(e) => setField('cancellation_effective_date', e.target.value)}
                      />
                    </Field>
                    <Field label="Time">
                      <div className="flex gap-1">
                        <Input
                          value={inputs.cancellation_effective_time}
                          onChange={(e) => setField('cancellation_effective_time', e.target.value)}
                          placeholder="12:01"
                          className="font-mono"
                        />
                        <select
                          value={inputs.cancellation_effective_meridian}
                          onChange={(e) => setField('cancellation_effective_meridian', e.target.value)}
                          className={cn(inputClass, 'w-auto')}
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                    </Field>
                  </div>
                </FieldSet>

                <FieldSet legend="Refund basis">
                  <Helper>
                    Ticking implies <em>zero commitment</em> to a return-premium amount.
                    The carrier always computes the actual refund.
                  </Helper>
                  <RadioGroup
                    name="lpr-basis"
                    value={inputs.basis}
                    options={BASIS_OPTIONS}
                    onChange={(v) => setField('basis', v)}
                  />
                </FieldSet>

                <FieldSet legend="Premium">
                  <Helper>
                    Full term auto-fills from the bound premium when blank.
                    Return premium and factor print only if you enter them,
                    blank means the carrier computes the refund. TRACON never
                    calculates these for you.
                  </Helper>
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="Full term $">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={inputs.full_term_premium}
                        onChange={(e) => setField('full_term_premium', e.target.value)}
                        placeholder="auto"
                      />
                    </Field>
                    <Field label="Return $">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={inputs.return_premium}
                        onChange={(e) => setField('return_premium', e.target.value)}
                        placeholder="carrier"
                      />
                    </Field>
                    <Field label="Unearned factor">
                      <Input
                        value={inputs.unearned_factor}
                        onChange={(e) => setField('unearned_factor', e.target.value)}
                        placeholder="carrier"
                      />
                    </Field>
                  </div>
                </FieldSet>

                <FieldSet legend="Reason">
                  <RadioGroup
                    name="lpr-reason"
                    value={inputs.reason}
                    options={REASON_OPTIONS}
                    onChange={(v) => setField('reason', v)}
                  />
                  {inputs.reason === 'rewritten' ? (
                    <Input
                      value={inputs.new_insurer_name}
                      onChange={(e) => setField('new_insurer_name', e.target.value)}
                      placeholder="New insurer name"
                      className="mt-2"
                    />
                  ) : null}
                  {inputs.reason === 'other' ? (
                    <Input
                      value={inputs.other_description}
                      onChange={(e) => setField('other_description', e.target.value)}
                      placeholder="Provide detail"
                      className="mt-2"
                    />
                  ) : null}
                </FieldSet>

                <FieldSet legend="Send copies to">
                  <div className="grid grid-cols-2 gap-2">
                    {DISTRIBUTION_OPTIONS.map((opt) => (
                      <label key={opt.key} className="flex items-center gap-2 text-sm text-text">
                        <input
                          type="checkbox"
                          checked={!!inputs.distribution[opt.key]}
                          onChange={(e) => setDistField(opt.key, e.target.checked)}
                          className="h-4 w-4 rounded border-border bg-bg accent-accent"
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                  {inputs.distribution.other ? (
                    <Input
                      value={inputs.distribution.other_description}
                      onChange={(e) =>
                        setInputs((prev) => ({
                          ...prev,
                          distribution: { ...prev.distribution, other_description: e.target.value },
                        }))
                      }
                      placeholder="Other recipient description"
                      className="mt-2"
                    />
                  ) : null}
                  <label className="mt-3 flex items-center gap-2 border-t border-border pt-3 text-xs text-text-muted">
                    <input
                      type="checkbox"
                      checked={inputs.subject_to_audit}
                      onChange={(e) => setField('subject_to_audit', e.target.checked)}
                      className="h-4 w-4 rounded border-border bg-bg accent-accent"
                    />
                    <span>Also mark <em>Subject to audit</em> (independent of basis)</span>
                  </label>
                </FieldSet>

                <fieldset className="rounded-card border border-warning bg-warning-soft p-3">
                  <legend className="px-1 text-[10px] font-semibold uppercase tracking-wider text-warning">
                    Broker notes — internal · prints on REMARKS
                  </legend>
                  <Helper>
                    Visible to the carrier on the printed form. NOT shown in the
                    client-facing email body.
                  </Helper>
                  <textarea
                    value={inputs.broker_notes}
                    onChange={(e) => setField('broker_notes', e.target.value)}
                    rows={3}
                    placeholder="Optional — anything the carrier needs alongside the release."
                    className={cn(inputClass, 'min-h-[88px] resize-y')}
                  />
                </fieldset>
              </div>

              {/* ── Right column: live preview ────────────────────── */}
              <div className="lg:sticky lg:top-0 lg:self-start">
                <PreviewPane
                  url={previewUrl}
                  loading={previewLoading}
                  error={previewError}
                  onFixPolicyInfo={() => setPolicyInfoOpen(true)}
                />
              </div>
            </div>

            {formError ? (
              <div className="mt-3 flex items-start gap-2 rounded-card border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                <AlertTriangle size={14} strokeWidth={1.75} className="mt-0.5 shrink-0" />
                <span>{formError}</span>
              </div>
            ) : null}
          </div>
        )}

        {done ? null : (
          <SheetFooter className="border-t border-border bg-surface px-4 py-3">
            <Button variant="ghost" onClick={() => onOpenChange?.(false)} disabled={submitting || savingDraft}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleSaveDraft}
              disabled={submitting || savingDraft}
            >
              {savingDraft ? 'Saving…' : 'Save draft'}
            </Button>
            <Button
              onClick={handleDispatch}
              disabled={submitting || savingDraft}
              className="bg-accent text-white hover:bg-accent/90"
            >
              {submitting ? 'Sending…' : 'Send for signature'}
            </Button>
          </SheetFooter>
        )}

        <PolicyInfoDialog
          dealId={dealId}
          open={policyInfoOpen}
          onOpenChange={setPolicyInfoOpen}
          initial={{
            policy_number: dealCtx?.policy_number || dealCtx?.Policy_Number,
            policy_start: dealCtx?.policy_start || dealCtx?.Policy_Start,
            policy_end: dealCtx?.policy_expiration || dealCtx?.policy_end || dealCtx?.Policy_End_Date,
            carrier_name: carrier,
          }}
          onSaved={() => {
            setCtxRefresh((n) => n + 1);
            if (typeof onChange === 'function') onChange();
          }}
        />
      </SheetContent>
    </Sheet>
  );
}

// ── Sub-components (module scope) ────────────────────────────────────

function PolicyCard({ expanded, onToggle, insured, carrier, policyNumber, policyStart, policyEnd, onEdit }) {
  return (
    <div className="rounded-card border border-border bg-surface">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-2.5 text-left hover:bg-bg/40 transition-colors"
      >
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">Policy</div>
          <div className="text-sm text-text truncate">
            <span className="font-medium">{insured || '—'}</span>
            <span className="text-text-muted"> · {carrier || '—'} · {policyNumber || '—'}</span>
          </div>
        </div>
        {expanded
          ? <ChevronDown size={16} strokeWidth={1.5} className="text-text-dim" />
          : <ChevronRight size={16} strokeWidth={1.5} className="text-text-dim" />}
      </button>
      {expanded ? (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-border px-3 py-3 text-xs">
          <KV label="Insured" value={insured || '—'} />
          <KV label="Carrier" value={carrier || '—'} />
          <KV label="Policy number" value={policyNumber || '—'} />
          <KV label="Effective" value={policyStart || '—'} />
          <KV label="Expiration" value={policyEnd || '—'} />
          {onEdit ? (
            <div className="col-span-2 border-t border-border pt-2">
              <button
                type="button"
                onClick={onEdit}
                className="text-[11px] font-medium text-accent hover:underline"
              >
                Edit policy info
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-text-dim">{label}</div>
      <div className="text-xs text-text truncate" title={String(value || '')}>{value}</div>
    </div>
  );
}

function FieldSet({ legend, children }) {
  return (
    <fieldset className="rounded-card border border-border bg-surface p-3">
      <legend className="px-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
        {legend}
      </legend>
      <div className="space-y-2">{children}</div>
    </fieldset>
  );
}

function Field({ label, className, children }) {
  return (
    <label className={cn('block', className)}>
      <span className="text-xs font-medium uppercase tracking-wide text-text-muted">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Helper({ children }) {
  return <p className="mb-2 text-[11px] text-text-dim">{children}</p>;
}

function RadioGroup({ name, value, options, onChange }) {
  return (
    <div className="space-y-2">
      {options.map((opt) => {
        const checked = value === opt.key;
        return (
          <label
            key={opt.key}
            className={cn(
              'flex cursor-pointer items-start gap-2 rounded-card border p-2.5 transition-colors',
              checked
                ? 'border-accent bg-accent-soft'
                : 'border-border hover:bg-bg/40'
            )}
          >
            <input
              type="radio"
              name={name}
              value={opt.key}
              checked={checked}
              onChange={() => onChange(opt.key)}
              className="mt-0.5 h-4 w-4 accent-accent"
            />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-text">{opt.label}</div>
              {opt.desc ? <div className="mt-0.5 text-[11px] text-text-muted">{opt.desc}</div> : null}
            </div>
          </label>
        );
      })}
    </div>
  );
}

function PreviewPane({ url, loading, error, onFixPolicyInfo }) {
  // The data mappers throw 'missing required field "policy.number"' style
  // errors for deal fields the broker can fix in place. Surface the door
  // right in the error strip instead of dead-ending.
  const fixable = !!error && /missing required field|no Carrier|policy info/i.test(String(error));
  return (
    <div className="flex flex-col overflow-hidden rounded-card border border-border bg-surface" style={{ minHeight: '520px' }}>
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">
          ACORD 35 preview
        </div>
        {loading ? <div className="text-[11px] text-text-dim">Rendering…</div> : null}
      </div>
      {error ? (
        <div className="border-b border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          <span>{error}</span>
          {fixable && onFixPolicyInfo ? (
            <button
              type="button"
              onClick={onFixPolicyInfo}
              className="ml-2 font-semibold text-red-200 underline hover:text-red-100"
            >
              Edit policy info
            </button>
          ) : null}
        </div>
      ) : null}
      {url ? (
        <iframe
          src={url}
          title="LPR preview"
          className="flex-1"
          style={{ minHeight: '480px', border: 'none', background: 'white' }}
        />
      ) : (
        <div className="flex flex-1 items-center justify-center text-sm text-text-muted" style={{ minHeight: '480px' }}>
          {loading ? 'Rendering preview…' : 'Preview will appear here.'}
        </div>
      )}
    </div>
  );
}

function SuccessBody({ done, carrier, onCopy, scoped }) {
  return (
    <div className="flex-1 overflow-y-auto px-4 py-6">
      <div className="mx-auto max-w-xl">
        <div className="rounded-card border border-success bg-success-soft p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-success">
            <CheckCircle2 size={16} strokeWidth={1.75} />
            <span>Release sent for signature</span>
          </div>
          <p className="mt-2 text-xs text-text-muted">
            {done.emailed_to ? (
              <>Emailed to <span className="text-text">{done.emailed_to}</span>. </>
            ) : ''}
            The insured can sign at the Tower link below.
            Carrier remits return premium to NDEMNIFLY (60-90 days); refund the insured from there.
            {scoped ? ' The roster stays unchanged until you confirm the carrier endorsement (return premium) on this transaction.' : ''}
          </p>
        </div>

        <dl className="mt-4 space-y-3 text-xs">
          {done.tower_url ? (
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">Tower link</dt>
              <dd className="mt-1 flex items-center gap-2">
                <a
                  href={done.tower_url}
                  target="_blank"
                  rel="noopener"
                  className="break-all text-info hover:underline"
                >
                  {done.tower_url}
                </a>
                <button
                  type="button"
                  onClick={onCopy}
                  className="rounded-card border border-border bg-surface p-1 text-text-muted hover:text-text"
                  aria-label="Copy link"
                >
                  <Copy size={12} strokeWidth={1.75} />
                </button>
              </dd>
            </div>
          ) : null}
          {done.pdf_url ? (
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">Unsigned PDF</dt>
              <dd className="mt-1">
                <a
                  href={done.pdf_url}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-1 text-info hover:underline"
                >
                  View in WorkDrive
                  <ExternalLink size={11} strokeWidth={1.75} />
                </a>
              </dd>
            </div>
          ) : null}
          {done.expires_at ? (
            <div className="text-[11px] text-text-dim">
              Tower link expires {formatDateTime(done.expires_at)}
            </div>
          ) : null}
        </dl>
      </div>
    </div>
  );
}
